import { useContext, useMemo, useState } from "react";

import { UserContext } from "@context/user";
import { BRAND_ENDPOINTS } from "@constants/endpoints";
import useContractReview from "@panel/shared/contract/hooks/use-contract-review";
import useDocumentViewer from "@panel/shared/contract/hooks/use-document-viewer";
import usePublisherContracts from "@panel/shared/contract/hooks/use-publisher-contracts";
import useBrandRouteGuard from "../../use-brand-route-guard";
import useBrandSign from "./use-brand-sign";

// What a publisher's contract is called on screen and when downloaded.
const documentNameOf = (row) => `Contract - ${row.name}`;

// Container hook for the brand Contract page: it holds every piece of state
// the screen needs and hands the view a flat, render-ready object, so
// index.jsx and everything under components/ stay free of hooks.
const useBrandContractPage = () => {
    const { brandId } = useBrandRouteGuard();
    const { brands } = useContext(UserContext);
    const {
        publishers,
        counts,
        pagination,
        loading,
        handlePageChange,
        handlePerRowsChange,
        refetch
    } = usePublisherContracts({
        base: "brand",
        path: brandId ? BRAND_ENDPOINTS.CONTRACT_PUBLISHERS(brandId) : ""
    });

    const brandName = useMemo(
        () => brands.find((brand) => brand.id === brandId)?.name,
        [brands, brandId]
    );

    const { signing, signingAll, signOne, handleSignAll } = useBrandSign(brandId, refetch);

    // The row whose contract is open for signing.
    const [reviewing, setReviewing] = useState(null);
    const documentPath = reviewing ? BRAND_ENDPOINTS.CONTRACT_DOCUMENT(brandId, reviewing.contractId) : "";

    // Signing one contract goes through the same gating as the publisher's —
    // the document has to have been shown before the signature is reachable.
    const { openReview, modal } = useContractReview({
        contract     : reviewing && { ...reviewing, name: documentNameOf(reviewing), exists: true },
        base         : "brand",
        documentPath,
        onAcknowledge: () => signOne(reviewing?.contractId)
    });

    // A contract the brand has already signed is only looked at, so it opens in
    // the read-only reader rather than the review-and-sign modal.
    const { openPreview, modal: previewModal } = useDocumentViewer("brand");

    return {
        brandName,
        counts,
        reviewModal: {
            ...modal,
            accepting: signing,
            subtitle : reviewing?.email || ""
        },
        previewModal,
        publisherTable: {
            loading,
            publishers,
            counts,
            pagination,
            handlePageChange,
            handlePerRowsChange,
            onRefresh : refetch,
            canSignAll: counts.awaitingBrand > 0,
            signingAll,
            onSignAll : () => handleSignAll(counts.awaitingBrand),
            onSign    : (row) => {
                setReviewing(row);
                openReview();
            },
            onView: (row) => openPreview(
                BRAND_ENDPOINTS.CONTRACT_DOCUMENT(brandId, row.contractId),
                documentNameOf(row),
                row.email
            )
        }
    };
};

export default useBrandContractPage;
